import { useState, useEffect, useCallback, useMemo } from 'react';
import type { AuditHistory, SiteHistory, HistoricalSnapshot, TrendData } from '@/types/audit';

const HISTORY_URL = '/data/audit-history.json';

interface UseAuditHistoryResult {
  history: AuditHistory | null;
  loading: boolean;
  error: Error | null;
  getSiteHistory: (siteId: string) => SiteHistory | undefined;
  getTrend: (siteId: string, metric: TrendMetric) => TrendData | null;
  getComparison: (siteId: string) => TrendComparison | null;
}

type TrendMetric =
  | 'performance'
  | 'seo'
  | 'accessibility'
  | 'bestPractices'
  | 'tracking'
  | 'lcp'
  | 'cls'
  | 'fcp'
  | 'ttfb'
  | 'recommendations'
  | 'criticalIssues';

interface TrendComparison {
  performance: TrendData;
  seo: TrendData;
  accessibility: TrendData;
  bestPractices: TrendData;
  tracking: TrendData;
  lcp: TrendData;
  cls: TrendData;
  fcp: TrendData;
  ttfb: TrendData;
  recommendations: TrendData;
  criticalIssues: TrendData;
}

function calculateTrend(current: number, previous: number): TrendData {
  const change = current - previous;
  const percentChange = previous === 0 ? 0 : Math.round((change / previous) * 100);
  const direction = Math.abs(change) < 1 ? 'stable' : change > 0 ? 'up' : 'down';
  return { direction, change, percentChange };
}

function getMetricValue(snapshot: HistoricalSnapshot, metric: TrendMetric): number {
  switch (metric) {
    case 'performance': return snapshot.performanceScore;
    case 'seo': return snapshot.seoScore;
    case 'accessibility': return snapshot.accessibilityScore;
    case 'bestPractices': return snapshot.bestPracticesScore;
    case 'tracking': return snapshot.trackingScore;
    case 'lcp': return snapshot.metrics.lcp;
    case 'cls': return snapshot.metrics.cls;
    case 'fcp': return snapshot.metrics.fcp;
    case 'ttfb': return snapshot.metrics.ttfb;
    case 'recommendations': return snapshot.recommendationCount;
    case 'criticalIssues': return snapshot.criticalIssueCount;
    default: return 0;
  }
}

export function useAuditHistory(): UseAuditHistoryResult {
  const [history, setHistory] = useState<AuditHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchHistory() {
      try {
        const response = await fetch(HISTORY_URL);

        if (!response.ok) {
          // History file may not exist yet - that's OK
          if (response.status === 404) {
            if (mounted) {
              setHistory(null);
              setLoading(false);
            }
            return;
          }
          throw new Error(`Failed to load history: ${response.status}`);
        }

        const data = await response.json();
        if (mounted) {
          setHistory(data as AuditHistory);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          // Don't treat 404 as an error
          setHistory(null);
          setLoading(false);
        }
      }
    }

    fetchHistory();

    return () => {
      mounted = false;
    };
  }, []);

  const getSiteHistory = useCallback((siteId: string): SiteHistory | undefined => {
    return history?.sites.find(s => s.siteId === siteId);
  }, [history]);

  const getTrend = useCallback((siteId: string, metric: TrendMetric): TrendData | null => {
    const siteHistory = getSiteHistory(siteId);
    if (!siteHistory || siteHistory.snapshots.length < 2) {
      return null;
    }

    const current = siteHistory.snapshots[siteHistory.snapshots.length - 1];
    const previous = siteHistory.snapshots[siteHistory.snapshots.length - 2];

    return calculateTrend(
      getMetricValue(current, metric),
      getMetricValue(previous, metric)
    );
  }, [getSiteHistory]);

  const getComparison = useCallback((siteId: string): TrendComparison | null => {
    const siteHistory = getSiteHistory(siteId);
    if (!siteHistory || siteHistory.snapshots.length < 2) {
      return null;
    }

    const current = siteHistory.snapshots[siteHistory.snapshots.length - 1];
    const previous = siteHistory.snapshots[siteHistory.snapshots.length - 2];

    return {
      performance: calculateTrend(current.performanceScore, previous.performanceScore),
      seo: calculateTrend(current.seoScore, previous.seoScore),
      accessibility: calculateTrend(current.accessibilityScore, previous.accessibilityScore),
      bestPractices: calculateTrend(current.bestPracticesScore, previous.bestPracticesScore),
      tracking: calculateTrend(current.trackingScore, previous.trackingScore),
      lcp: calculateTrend(current.metrics.lcp, previous.metrics.lcp),
      cls: calculateTrend(current.metrics.cls, previous.metrics.cls),
      fcp: calculateTrend(current.metrics.fcp, previous.metrics.fcp),
      ttfb: calculateTrend(current.metrics.ttfb, previous.metrics.ttfb),
      recommendations: calculateTrend(current.recommendationCount, previous.recommendationCount),
      criticalIssues: calculateTrend(current.criticalIssueCount, previous.criticalIssueCount),
    };
  }, [getSiteHistory]);

  return { history, loading, error, getSiteHistory, getTrend, getComparison };
}

// Hook for getting chart data formatted for recharts
export function useSiteHistoryChartData(siteId: string) {
  const { getSiteHistory, loading, error } = useAuditHistory();

  const chartData = useMemo(() => {
    const siteHistory = getSiteHistory(siteId);
    if (!siteHistory) return [];

    return siteHistory.snapshots.map(snapshot => ({
      date: new Date(snapshot.auditedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }),
      fullDate: snapshot.auditedAt,
      performance: snapshot.performanceScore,
      seo: snapshot.seoScore,
      accessibility: snapshot.accessibilityScore,
      bestPractices: snapshot.bestPracticesScore,
      tracking: snapshot.trackingScore,
      lcp: snapshot.metrics.lcp,
      cls: snapshot.metrics.cls,
      fcp: snapshot.metrics.fcp,
      ttfb: snapshot.metrics.ttfb,
      recommendations: snapshot.recommendationCount,
      criticalIssues: snapshot.criticalIssueCount,
    }));
  }, [getSiteHistory, siteId]);

  return { chartData, loading, error };
}
